import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import api from "../../api";
import "react-toastify/dist/ReactToastify.css";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatMoney = (v) => `₹${Number(v || 0).toLocaleString()}`;

const SalaryPayrollPage = () => {

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [months, setMonths] = useState([]);

  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [amount, setAmount] = useState("");
  const [adjustments, setAdjustments] = useState([]);
  const [adjType, setAdjType] = useState("");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjNote, setAdjNote] = useState("");

  useEffect(() => { loadEmployees(); }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const res = await api.get("/teachers/list");

      setEmployees(res.data.data.map(e => ({
        value: e.id,
        label: e.name,
        data: e
      })));

    } catch {
      toast.error("Failed loading employees");
    }
    setLoading(false);
  };

  const addAdjustment = () => {
    if (!adjType || !adjAmount) {
      toast.error("Select type & enter amount");
      return;
    }

    setAdjustments([
      ...adjustments,
      {
        type: adjType,
        amount: parseFloat(adjAmount),
        note: adjNote
      }
    ]);

    setAdjType("");
    setAdjAmount("");
    setAdjNote("");
  };

  const removeAdjustment = (index) => {
    const updated = adjustments.filter((_, i) => i !== index);
    setAdjustments(updated);
  };

  const loadMonthlyStatus = async (empId) => {
    try {
      setLoading(true);
      const res = await api.get(`/salary/monthly-status?employee_id=${empId}`);
      setMonths(res.data.data);
    } catch {
      toast.error("Failed loading months");
    }
    setLoading(false);
  };

  const selectEmployee = (opt) => {
    if (!opt) {
      setSelectedEmployee(null);
      setMonths([]);   // optional: reset month cards
      return;
    }

    setSelectedEmployee(opt);
    loadMonthlyStatus(opt.value);
  };

  const openPayModal = (month) => {
    setSelectedMonth(month);
    setAmount();
    setShowModal(true);
  };

  const paySalary = async () => {
    const finalPayable = calculatedFinal();
    const payAmount = Number(amount);

    if (!payAmount || payAmount <= 0) {
      toast.error("Enter valid payment amount");
      return;
    }

    if (payAmount > finalPayable) {
      toast.error("Payment exceeds final payable amount");
      return;
    }

    try {
      setPayLoading(true);

      const res = await api.post("/salary/add", {
        employee_id: selectedEmployee.value,
        salary_month: selectedMonth.month,
        salary_paid: payAmount,
        adjustments: adjustments   // 🔥 important
      });

      if (res.data.status) {
        toast.success("Payment successful");

        // reset modal state
        setAdjustments([]);
        setAmount("");
        setShowModal(false);

        loadMonthlyStatus(selectedEmployee.value);
      } else {
        toast.error(res.data.message || "Payment failed");
      }

    } catch (err) {
      toast.error("Server error");
    }

    setPayLoading(false);
  };

  const renderStatus = (m) => {
    if (!m.month) return null;

    if (m.due === 0 && m.paid > 0)
      return <span style={{ color: "#fff" }}>Paid</span>;

    if (m.paid > 0 && m.due > 0)
      return <span style={{ color: "#fff" }}>Partial</span>;

    if (m.due > 0)
      return <span style={{ color: "#fff" }}>Unpaid</span>;

    return null;
  };

  const getStatusType = (m) => {
    if (!m || m.total_salary == null) return "empty";
    if (m.due == 0 && m.paid > 0) return "paid";
    if (m.paid > 0 && m.due > 0) return "partial";
    if (m.paid == 0 && m.due > 0) return "unpaid";
    return "empty";
  };

  const Info = ({ label, value }) => (
    <div style={infoItem}>
      <span style={infoLabel}>{label}</span>
      <span style={infoValue}>{value}</span>
    </div>
  );

  const calculatedFinal = () => {
    const base = Number(selectedMonth?.due || 0);

    let total = base;

    adjustments.forEach(adj => {
      const amt = Number(adj.amount);

      if (
        adj.type === "bonus" ||
        adj.type === "extra_class" ||
        adj.type === "incentive" ||
        adj.type === "overtime" ||
        adj.type === "arrear"
      ) {
        total += amt;
      } else {
        total -= amt;
      }
    });

    return total;
  };

  useEffect(() => {
    if (selectedMonth) {
      setAmount(calculatedFinal());
    }
  }, [adjustments]);


  const handleSalaryPrint = async (employeeId, month) => {
    try {
      const res = await api.get(`/salary-invoice/${employeeId}/${month}`);

      if (!res.data.status) {
        alert("Salary slip not found.");
        return;
      }

      const data = res.data.data;

      const printWindow = window.open("", "_blank");

      const LOGO_BASE = "https://dgps.co.in/public/admin/assets/img/schools/logo";
      const defaultLogo = "https://dgps.co.in/public/admin/assets/img/logo.png";

      const resolveLogo = (logo) => {
        if (!logo) return defaultLogo;
        if (/^https?:\/\//i.test(logo) || logo.startsWith("data:")) return logo;
        return `${LOGO_BASE}/${logo.replace(/^\/+/, "")}`;
      };

      const school = {
        name: (data.school_name || "SCHOOL NAME").toUpperCase(),
        address: data.school_address || "",
        phone: data.school_phone || "",
        email: data.school_email || "",
        logo: resolveLogo(data.school_logo),
        defaultLogo
      };

      const earnings = data.adjustments.filter(a =>
        ["bonus", "extra_class", "incentive", "arrear", "overtime", "other"]
          .includes(a.adjustment_type)
      );

      const deductions = data.adjustments.filter(a =>
        ["deduction", "penalty", "advance_adjust", "leave_deduction"]
          .includes(a.adjustment_type)
      );

      const earningRows = earnings.map(a => `
      <tr>
        <td>${a.adjustment_type.replace("_", " ").toUpperCase()}</td>
        <td style="text-align:right;">₹ ${parseFloat(a.amount).toFixed(2)}</td>
      </tr>
    `).join("");

      const deductionRows = deductions.map(a => `
      <tr>
        <td>${a.adjustment_type.replace("_", " ").toUpperCase()}</td>
        <td style="text-align:right;">₹ ${parseFloat(a.amount).toFixed(2)}</td>
      </tr>
    `).join("");

      function renderCopy(copyType) {
        return `
  <div class="copy">

    <div class="top-header">
      <div class="logo-area">
        <img src="${school.logo}" 
             onerror="this.src='${school.defaultLogo}'" 
             class="logo" />
        <div class="school-info">
          <div class="school-name">${school.name}</div>
          <div class="school-contact">
            ${school.address}<br>
            ${school.phone}${school.email ? " | " + school.email : ""}
          </div>
        </div>
      </div>
      <div class="copy-label">${copyType}</div>
    </div>

    <div class="title-bar">
      SALARY PAY SLIP – ${data.salary_month}
    </div>

    <div class="info-row">
      <div class="info-col">
        <div><span class="label">Employee Name:</span> ${data.employee_name}</div>
        <div><span class="label">Employee ID:</span> EMP#10${data.employee_id}</div>
        <div><span class="label">Designation:</span> ${data.department}</div>
      </div>

      <div class="info-col">
        <div><span class="label">Payment Mode:</span> ${data.payment_mode || "Cash"}</div>
        <div><span class="label">Generated Date:</span> ${new Date().toLocaleDateString()}</div>
      </div>
    </div>

    <div class="table-wrapper">
      <table class="salary-table">
        <tr>
          <th>Earnings</th>
          <th class="amount">Amount (₹)</th>
        </tr>
        <tr>
          <td>Base Salary</td>
          <td class="amount">${data.base_salary}</td>
        </tr>
        ${earningRows}
      </table>

      <table class="salary-table">
        <tr>
          <th>Deductions</th>
          <th class="amount">Amount (₹)</th>
        </tr>
        ${deductionRows || `<tr><td colspan="2" class="amount">0.00</td></tr>`}
      </table>
    </div>
    <div class="total-wrapper">
      <div class="total-section">
        <div>
          <span>Total Paid</span>
          <span>₹ ${data.total_paid}</span>
        </div>
        <div>
          <span>Closing Due</span>
          <span>₹ ${data.closing_due}</span>
        </div>
        <div class="net">
          <span>Net Salary</span>
          <span>₹ ${data.net_salary}</span>
        </div>
      </div>
    </div>
    <div class="footer">
      <div>Authorized Signature</div>
      <div>This is a system generated salary slip.</div>
    </div>

  </div>
  `;
      }
      printWindow.document.write(`
  <html>
  <head>
  <title>Salary Slip</title>

  <style>
    @page {
      size: A4;
      margin: 5mm;
    }

    body {
      font-family: "Segoe UI", Arial, sans-serif;
      margin: 0;
      font-size: 12px;
      background: #f4f6f8;
      color: #2c2c2c;
    }

    .page {
      width: 100%;
    }

    .copy {
      background: #ffffff;
      padding: 12px;
      border: 1px solid #dcdcdc;
      border-radius: 6px;
      margin-bottom: 10px;
    }

    .cut-line {
      border-top: 2px dashed #b5b5b5;
      margin: 10px 0;
    }

    /* HEADER */
    .top-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e5e5e5;
      padding-bottom: 10px;
    }

    .logo-area {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .logo {
      width: 65px;
      height: 65px;
      object-fit: contain;
    }

    .school-info {
      line-height: 1.4;
    }

    .school-name {
      font-size: 17px;
      font-weight: 700;
      letter-spacing: .5px;
      color: #1f2d3d;
    }

    .school-contact {
      font-size: 11px;
      color: #666;
    }

    .copy-label {
      font-size: 11px;
      font-weight: 600;
      color: #9a9a9a;
    }

    /* TITLE BAR */
    .title-bar {
      margin-top: 14px;
      background: #1e3a5f;
      color: #fff;
      padding: 9px 14px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: .8px;
      border-radius: 4px;
    }

    /* INFO SECTION */
    .info-row {
      display: flex;
      justify-content: space-between;
      margin-top: 16px;
    }

    .info-col {
      width: 48%;
    }

    .info-col div {
      margin-bottom: 7px;
    }

    .label {
      font-weight: 600;
      display: inline-block;
      width: 125px;
      color: #444;
    }

    /* TABLES */
    .table-wrapper {
      display: flex;
      justify-content: space-between;
      margin-top: 12px;
      position: relative;
    }

    .salary-table {
      width: 48%;
      border-collapse: collapse;
      font-size: 12px;
    }

    .salary-table th,
    .salary-table td {
      border: 1px solid #e0e0e0;
      padding: 5px;
    }

    .salary-table th {
      background: #2b4c77;
      color: #fff;
      font-weight: 600;
      font-size: 12px;
    }

    .salary-table tr:nth-child(even) {
      background: #f9fbfd;
    }

    .amount {
      text-align: right;
      font-weight: 500;
    }

    /* TOTAL BLOCK */
    .total-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
      width: 100%;
    }
    .total-section {
      width: 300px;
      margin-left: auto;   /* THIS IS THE REAL FIX */
      margin-right: 0;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
    }

    .total-section div {
      padding: 6px 10px;
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid #eee;
    }

    .total-section div:last-child {
      border-bottom: none;
    }

    .total-section .net {
      background: #eef3f8;
      font-weight: 700;
      font-size: 13px;
      color: #1e3a5f;
    }

    /* FOOTER */
    .footer {
      display: flex;
      justify-content: space-between;
      margin-top: 25px;
      font-size: 11px;
      color: #666;
    }

    @media print {
      body {
        background: white;
        padding: 0;
      }

    }
  </style>
  </head>

  <body>
  <div class="page">

  ${renderCopy("OFFICE COPY")}
  <div class="cut-line"></div>
  ${renderCopy("EMPLOYEE COPY")}

  </div>

  <script>
  window.onload = function(){
    window.print();
  };
  window.onafterprint = function(){
    window.close();
  };
  </script>

  </body>
  </html>
  `);

      printWindow.document.close();

    } catch (error) {
      console.error("Salary print error:", error);
      alert("Something went wrong while generating the salary slip.");
    }
  };

  return (
    <div style={{ padding: 0, background: "#eef2f7", minHeight: "100vh" }}>
      <ToastContainer />

      <div style={container}>

        <h2 style={{ textAlign: "center", marginBottom: 15 }}>Salary Payroll</h2>

        <Select
          options={employees}
          value={selectedEmployee}
          onChange={selectEmployee}
          isClearable
          placeholder="Select Employee"
        />

        {loading && <div style={{ textAlign: "center", marginTop: 20 }}><FaSpinner className="spin" /></div>}

        {selectedEmployee && (

          <div style={gridWrap}>

            {/* PROFILE */}
            <div style={profileCard}>
              <span style={statusBadge}>
                {selectedEmployee.data.status.toUpperCase()}
              </span>

              {/* Avatar */}
              <div style={avatarWrapper}>
                <img
                  src={`https://dgps.co.in/${selectedEmployee.data.photo}`}
                  alt={selectedEmployee.data.name}
                  style={avatar}
                />
              </div>

              {/* Identity */}
              <h2 style={name}>{selectedEmployee.data.name}</h2>

              <div style={roleRow}>
                <span style={designationBadge}>
                  {selectedEmployee.data.designation}
                </span>
                <span style={typeBadge}>
                  {selectedEmployee.data.employee_type}
                </span>
              </div>

              <p style={department}>{selectedEmployee.data.department}</p>

              <div style={divider}></div>

              {/* Info */}
              <div style={infoGrid}>
                <Info label="Mobile" value={selectedEmployee.data.mobile} />
                <Info label="Father" value={selectedEmployee.data.father_name} />
                <Info label="DOB" value={selectedEmployee.data.dob} />
                <Info label="Gender" value={selectedEmployee.data.gender} />
                <Info label="Blood" value={selectedEmployee.data.blood} />
                <Info label="Group" value={selectedEmployee.data.employee_group} />
                <Info label="Qualification" value={selectedEmployee.data.qualification} />
                <Info label="Joining" value={selectedEmployee.data.joining_date} />
              </div>

              <div style={divider}></div>

              {/* Address */}
              <div style={addressBox}>
                <div style={addressTitle}>Address</div>
                <div style={addressText}>
                  {selectedEmployee.data.village}, {selectedEmployee.data.locality}<br />
                  PO: {selectedEmployee.data.post_office}<br />
                  PS: {selectedEmployee.data.police_station}<br />
                  {selectedEmployee.data.block_name}, {selectedEmployee.data.city_name}<br />
                  {selectedEmployee.data.state_name} - {selectedEmployee.data.pincode}
                </div>
              </div>
            </div>

            {/* MONTH CARDS */}
            <div style={monthsGrid}>
              {monthNames.map((name, i) => {
                const m = months.find(x => parseInt(x.month.split('-')[1]) === i + 1) || {};
                const statusType = getStatusType(m);

                return (
                  <div
                    key={i}
                    style={{
                      ...monthCard,
                      ...statusStyles[statusType]
                    }}
                    className="monthCard"
                  >
                    <div style={cardContent}>

                      <div>
                        <div style={monthHeader}>
                          <b>{name}</b>
                          <span style={{ ...statusBadgeM, ...badgeStyles[statusType] }}>
                            {renderStatus(m)}
                          </span>
                        </div>

                        <div style={summaryBox}>

                          <div style={summaryRow}>
                            <span style={baseLabel}>Salary</span>
                            <span style={baseValue}>
                              {formatMoney(m.base_salary)}
                            </span>
                          </div>

                          <div style={{ ...summaryRow, ...netRow }}>
                            <span style={netLabel}>Net Salary</span>
                            <span style={netValue}>
                              {formatMoney(m.total_salary)}
                            </span>
                          </div>

                          <div style={summaryRow}>
                            <span style={paidLabel}>Paid</span>
                            <span style={paidValue}>{formatMoney(m.paid)}</span>
                          </div>

                          <div style={summaryRow}>
                            <span style={dueLabel}>Due</span>
                            <span style={dueValue}>{formatMoney(m.due)}</span>
                          </div>

                        </div>

                      </div>

                      <div style={actionArea}>
                        {statusType === "paid" && (
                          <button
                            onClick={() => handleSalaryPrint(m.employee_id, m.month)}
                            style={invoiceBtn}
                          >
                            View Invoice
                          </button>
                        )}

                        {m.due > 0 && (
                          <button
                            onClick={() => openPayModal(m)}
                            style={payBtnM}
                          >
                            Pay Now
                          </button>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {/* MODAL */}
      {showModal && (
        <div style={modalBg}>
          <div style={{ ...modal, width: 420 }}>

            <div style={modalHeader}>
              <h2 style={{ margin: 0 }}>Pay Salary</h2>
              <span style={monthText}>{selectedMonth.month}</span>
            </div>

            <div style={dueBox}>
              Due Amount
              <div style={dueAmount}>{formatMoney(selectedMonth.due)}</div>
            </div>

            {/* Payment Amount */}
            <input
              type="text"
              placeholder="Enter payment amount"
              value={amount}
              onChange={e => setAmount(e.target.value, "number")}
              style={input}
            />

            <button
              onClick={() => setAmount(calculatedFinal())}
              style={fullBtn}
            >
              Pay Final Payable
            </button>

            {/* ================== ADJUSTMENT SECTION ================== */}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Add Adjustment
              </div>

              <select
                value={adjType}
                onChange={e => setAdjType(e.target.value)}
                style={input}
              >
                <option value="">Select Type</option>
                <option value="bonus">Bonus</option>
                <option value="extra_class">Extra Class</option>
                <option value="incentive">Incentive</option>
                <option value="overtime">Overtime</option>
                <option value="arrear">Arrear</option>
                <option value="leave_deduction">Leave Deduction</option>
                <option value="penalty">Penalty</option>
                <option value="advance_adjust">Advance Adjust</option>
                <option value="other">Other</option>
              </select>

              <input
                type="text"
                placeholder="Enter Adjustment Amount"
                value={adjAmount}
                onChange={e => setAdjAmount(e.target.value, "number")}
                style={{ ...input, marginTop: 6 }}
              />

              <input
                type="text"
                placeholder="Note (optional)"
                value={adjNote}
                onChange={e => setAdjNote(e.target.value)}
                style={{ ...input, marginTop: 6 }}
              />

              <button
                onClick={addAdjustment}
                style={{
                  marginTop: 8,
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  padding: 8,
                  borderRadius: 8,
                  cursor: "pointer",
                  width: "100%",
                  fontWeight: 600
                }}
              >
                Add Adjustment
              </button>
            </div>

            {/* Adjustment List */}
            {adjustments.length > 0 && (
              <div style={{
                marginTop: 10,
                background: "#f9fafb",
                padding: 10,
                borderRadius: 10,
                maxHeight: 120,
                overflowY: "auto"
              }}>
                {adjustments.map((a, i) => (
                  <div key={i} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    marginBottom: 4
                  }}>
                    <span>
                      {a.type} — ₹{a.amount}
                    </span>
                    <button
                      onClick={() => removeAdjustment(i)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#dc2626",
                        cursor: "pointer"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 10,
              background: "#f3f4f6",
              textAlign: "center"
            }}>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                Final Payable
              </div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {formatMoney(calculatedFinal())}
              </div>
            </div>

            {/* Buttons */}
            <div style={btnRow}>
              <button
                disabled={payLoading}
                onClick={paySalary}
                style={payBtn}
              >
                {payLoading ? <FaSpinner className="spin" /> : "Pay Now"}
              </button>

              <button
                onClick={() => setShowModal(false)}
                style={cancelBtn}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  )
};

/* ================= STYLES ================= */

const container = {
  maxWidth: 1100,
  margin: "auto",
  background: "#fff",
  padding: 25,
  borderRadius: 16
};

const gridWrap = {
  display: "grid",
  gridTemplateColumns: "320px 1fr",
  gap: 20,
  marginTop: 20
};

const profileCard = {
  background: "#ffffff",
  padding: 26,
  borderRadius: 20,
  textAlign: "center",
  boxShadow: "0 15px 40px rgba(0,0,0,0.08)",
  maxWidth: 320,
  position: "relative",
  border: "1px solid rgba(0,0,0,0.04)"
};

const avatarWrapper = {
  display: "flex",
  justifyContent: "center",
  marginBottom: 10
};

const avatar = {
  width: 110,
  height: 110,
  borderRadius: "50%",
  objectFit: "cover",
  border: "4px solid white",
  boxShadow: "0 6px 18px rgba(0,0,0,0.15)"
};

const roleRow = {
  display: "flex",
  justifyContent: "center",
  gap: 6,
  marginTop: 6
};

const typeBadge = {
  background: "#ecfeff",
  color: "#0e7490",
  padding: "4px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600
};

const department = {
  marginTop: 8,
  fontWeight: 600,
  color: "#4b5563",
  letterSpacing: ".3px"
};

const infoGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px 14px",
  textAlign: "left",
  marginTop: 4
};

const infoItem = {
  display: "flex",
  flexDirection: "column"
};

const infoLabel = {
  fontSize: 11,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: ".5px"
};

const infoValue = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827"
};

const name = {
  margin: "8px 0 2px",
  fontSize: 20,
  fontWeight: 700,
  color: "#111827"
};

const designationBadge = {
  background: "#eef2ff",
  color: "#3730a3",
  padding: "4px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600
};


const divider = {
  height: 1,
  background: "#e5e7eb",
  margin: "14px 0"
};

const addressBox = {
  textAlign: "left",
  background: "#f9fafb",
  padding: 12,
  borderRadius: 10
};

const addressTitle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#6b7280",
  marginBottom: 4,
  textTransform: "uppercase"
};

const addressText = {
  fontSize: 13,
  color: "#374151",
  lineHeight: "20px"
};

const statusBadge = {
  position: "absolute",
  top: 16,
  right: 16,
  background: "#dcfce7",
  color: "#166534",
  padding: "5px 12px",
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".5px"
};

const monthsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
  gap: 14
};

const monthCard = {
  borderRadius: 14,
  padding: 14,
  background: "#ffffff",
  transition: "all .25s ease",
  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  border: "1px solid #eef0f4",
  minHeight: 140,   // 🔥 keeps compact but balanced
};

const cardContent = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%"
};

const actionArea = {
  marginTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: 8
};

const invoiceBtn = {
  background: "#10b981",
  color: "#fff",
  border: "none",
  padding: "7px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
};

const payBtnM = {
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff",
  border: "none",
  padding: "8px",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  width: "100%",
};

const statusStyles = {
  paid: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0"
  },
  partial: {
    background: "#fffbeb",
    border: "1px solid #fde68a"
  },
  unpaid: {
    background: "#fef2f2",
    border: "1px solid #fecaca"
  },
  empty: {}
};

const badgeStyles = {
  paid: { background: "#16a34a", color: "#fff" },
  partial: { background: "#f59e0b", color: "#fff" },
  unpaid: { background: "#dc2626", color: "#fff" },
  empty: {}
};

const statusBadgeM = {
  fontSize: 11,
  padding: "3px 8px",
  borderRadius: 20,
  fontWeight: 600
};
const monthHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8
};

const summaryBox = {
  marginTop: 6,
  paddingTop: 6,
  borderTop: "1px dashed rgba(0,0,0,0.08)"
};

const summaryRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: 13,
  marginBottom: 4
};

const netRow = {
  background: "#eff6ff",
  padding: "4px 6px",
  borderRadius: 6
};

const baseLabel = {
  color: "#6b7280",
  fontWeight: 500
};

const baseValue = {
  fontWeight: 600,
  color: "#374151"   // soft grey
};

const netLabel = {
  color: "#2563eb",
  fontWeight: 600
};

const netValue = {
  fontWeight: 700,
  color: "#1d4ed8"   // stronger blue
};

const paidLabel = {
  color: "#059669",
  fontWeight: 600
};

const paidValue = {
  color: "#059669",
  fontWeight: 700
};

const dueLabel = {
  color: "#dc2626",
  fontWeight: 600
};

const dueValue = {
  color: "#dc2626",
  fontWeight: 700
};

const fullBtn = {
  width: "100%",
  margin: "6px 0 10px",
  background: "#16a34a",
  color: "#fff",
  border: "none",
  padding: "8px",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600
};

const cancelBtn = {
  flex: 1,
  background: "#ef4444",
  color: "#fff",
  border: "none",
  padding: "10px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 15
};

const modalBg = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.45)",
  backdropFilter: "blur(3px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999
};
const modal = {
  background: "#ffffff",
  padding: 26,
  borderRadius: 18,
  width: 360,
  maxWidth: "92%",
  boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
  animation: "fadeIn .25s ease",
  display: "flex",
  flexDirection: "column",
  gap: 12
};

const modalHeader = {
  textAlign: "center",
  marginBottom: 6
};

const monthText = {
  fontSize: 13,
  color: "#6b7280"
};

const dueBox = {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  borderRadius: 10,
  padding: 10,
  textAlign: "center",
  fontSize: 13,
  color: "#9a3412"
};

const dueAmount = {
  fontSize: 20,
  fontWeight: 700,
  marginTop: 2,
  color: "#dc2626"
};
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: 12,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  fontSize: 15,
  outline: "none",
  textAlign: "center"
};

const switchRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 4
};

const switchGroup = {
  display: "flex",
  gap: 6
};

const adjustBtn = (active) => ({
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid",
  borderColor: active ? "#2563eb" : "#e5e7eb",
  background: active ? "#eff6ff" : "#fff",
  color: active ? "#1d4ed8" : "#374151",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  transition: ".2s"
});

const btnRow = {
  display: "flex",
  gap: 10,
  marginTop: 10
};

const payBtn = {
  flex: 1,
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff",
  border: "none",
  padding: "10px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 15
};
export default SalaryPayrollPage;